import {
  makeStyles,
  mergeClasses,
  shorthands,
} from "@fluentui/react-components";
import type { AppPreviewGalleryState } from "./AppPreviewGallery.types";
import { customTokens } from "@1js/acui-theme";
// eslint-disable-next-line no-restricted-imports
import { HighContrastSelector } from "@1js/acui-common/lib/styling/next";

const useAppPreviewGalleryRootStyles = makeStyles({
  base: {
    backgroundColor: customTokens.colorNeutralBackground1,
  },
});

const useSizeStyles = makeStyles({
  // preview gallery can't be small
  medium: {
    height: "30px",
  },
  large: {
    height: "60px",
  },
});

const useAppPreviewGalleryHiddenFlyoutAnchorStyles = makeStyles({
  base: {
    visibility: "hidden",
    position: "absolute",
  },
});

const useAppPreviewGalleryActionFlyoutAnchorStyles = makeStyles({
  base: {
    position: "relative",
    display: "flex !important",
    opacity: "0px",
    gap: customTokens.spacingVerticalNone,
  },
  /*
 This gallery is tested for RibbonIconPreviewGallery and may need to be updated for others in future.
*/
  /*
 Removed for Medium sized as it's not needed for medium sized preview gallery
*/

  large: {
    width: customTokens.spacingHorizontalL,
    height: "60px",
    padding: `${customTokens.spacingVerticalM} ${customTokens.spacingHorizontalXS}`,
  },
});

/**
 * Container wraps around the Button Display and the Flyout Anchor components.
 * These styles are placing the components in a flex box
 */
const useAppPreviewGalleryContainerStyles = makeStyles({
  base: {
    display: "flex",
    ...shorthands.border(
      customTokens.strokeWidthThin,
      "solid",
      customTokens.colorNeutralStroke1
    ),
    borderRadius: customTokens.borderRadiusMedium,

    [`${HighContrastSelector}`]: {
      ...shorthands.outline(
        customTokens.strokeWidthThin,
        "solid",
        "ButtonText"
      ),
      outlineOffset: `calc(0px - ${customTokens.strokeWidthThin})`,
      borderBottom: "none",
      borderTop: "none",
      borderRight: "none",
      borderRightTopRadius: customTokens.borderRadiusNone,
      borderRightBottomRadius: customTokens.borderRadiusNone,
    },
  },
});

export const useAppPreviewGalleryStyles: (
  state: AppPreviewGalleryState
) => AppPreviewGalleryState = (state) => {
  const rootStyles = useAppPreviewGalleryRootStyles();
  const sizeStyles = useSizeStyles();
  const hiddenFlyoutAnchorStyles =
    useAppPreviewGalleryHiddenFlyoutAnchorStyles();
  const actionFlyoutAnchorStyles =
    useAppPreviewGalleryActionFlyoutAnchorStyles();
  const containerStyles = useAppPreviewGalleryContainerStyles();

  state.root.className = mergeClasses(
    rootStyles.base,
    state.size === "large" ? sizeStyles.large : sizeStyles.medium,
    state.root.className
  );

  state.hiddenFlyoutAnchor.className = mergeClasses(
    hiddenFlyoutAnchorStyles.base,
    state.hiddenFlyoutAnchor.className
  );

  state.actionFlyoutAnchor.className = mergeClasses(
    actionFlyoutAnchorStyles.base,
    state.size === "large" && actionFlyoutAnchorStyles.large,
    state.actionFlyoutAnchor.className
  );
  state.container.className = mergeClasses(
    containerStyles.base,
    state.container.className
  );

  return state;
};
